import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { line, Color, Size, pageKindLineText, t, type OrgChartNode } from '@bms2/shared';
import { Button, DatePicker, Pill, Select, TextInput } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ReadOnlyNote } from '../../components/composites';
import { Modal } from '../../components/modals/Modal';
import { PageModule } from '../../shell/PageModule';
import { useDocumentToday } from '../../shell/ViewerZone';
import { post, useApi, writeGate } from '../records/api';
import { OrgTree } from './OrgTree';
import styles from './OrgChartPage.module.css';
import { useNotice } from '../../components/controls/useNotice';

/**
 * The org chart at `/org` (piece 10; decision 0079). 🔨 It is the entry point
 * for a reporting-line change and not a report: a person is dragged onto their
 * new manager, or moved through the card's own action, and the write is one
 * dated position rather than a field edit.
 *
 * 🔨 The chart is drawn as a condensed hierarchy and never as an indented list
 * (decision 0463): the elbow between two cards is the reporting line, and a
 * report who manages nobody stacks under their manager rather than taking a
 * column of their own.
 *
 * 🔨 A person under nobody stands in an Unplaced group above the tree with the
 * queue's own link beside it, because a broken line is the thing the chart
 * exists to make visible. A reader without the capability sees the tree and no
 * action at all.
 */
export const ORG_CHART_ROUTE = '/org';
export const ORG_EXCEPTIONS_ROUTE = '/org/exceptions';
export const ORG_CHART_PATH = '/org/chart';
export const ORG_MOVE_PATH = '/org/move';
export const ORG_DEPARTMENT_HEAD_PATH = '/org/department-head';
export const ORG_EXCEPTIONS_PATH = '/org/exceptions';

export interface OrgDepartmentRow {
  id: string;
  name: string;
  headEmploymentId: string | null;
  headName: string | null;
}

export interface OrgChartAnswer {
  rootEmploymentId: string | null;
  nodes: OrgChartNode[];
  unplaced: OrgChartNode[];
  onDate: string;
  canEdit: boolean;
  departments: OrgDepartmentRow[];
  exceptionCount: number;
  /** 🔒 The active levels the Move window offers (T441; decision 1123); absent from an older reply. */
  skillLevels?: { id: string; name: string; level: number }[];
}

interface MoveDraft {
  employmentId: string;
  personName: string;
  bossEmploymentId: string;
  departmentId: string;
  effectiveOn: string;
  reason: string;
  replacementHeadEmploymentId: string;
  /** 🔒 The level the new position carries, opened on the level held; empty is No Level (T441; decision 1123). */
  skillLevelId: string;
  /** The level held, so a level retired since is still offered as the one held. */
  heldLevel: { id: string; name: string } | null;
}

interface HeadDraft {
  departmentId: string;
  departmentName: string;
  headEmploymentId: string;
  reason: string;
}

/** Every node the chart knows about, placed or not, so a picker offers the whole cast. */
function everyone(answer: OrgChartAnswer): OrgChartNode[] {
  return [...answer.nodes, ...answer.unplaced].sort((a, b) => a.label.localeCompare(b.label));
}

/** The caption a chart row carries: the title, the level (T441; decision 1123), the department, and how many report to them. */
export function nodeMeta(node: OrgChartNode): string {
  const reports = node.directReports > 0 ? `${node.directReports} ${t('org.reports')}` : null;
  return line(...[node.title, node.skillLevelName, node.departmentName, reports].filter(Boolean).map((part, index) => (index === 0 ? String(part) : `· ${part}`)));
}

export function OrgChartPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  /**
   * 🔨 Today is the viewer's own day (decision 0261). 💰 The page read the
   * machine's clock, so a chart opened at ten at night in one zone asked for
   * tomorrow's tree and defaulted a move to tomorrow, which the API refuses as
   * a future date (found in review, 2026-09-15).
   */
  const today = useDocumentToday();
  const [onDate, setOnDate] = useState(today);
  const fetched = useApi<OrgChartAnswer>(`${ORG_CHART_PATH}?on=${onDate}`, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const [move, setMove] = useState<MoveDraft | null>(null);
  const [head, setHead] = useState<HeadDraft | null>(null);
  const answer = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const mayEdit = writeGate(fetched.state, 'canEdit');
  const cast = useMemo(() => (answer ? everyone(answer) : []), [answer]);
  /**
   * 🔨 Who heads which department, read off the same rows the Departments
   * section lists, so a head's badge and that section can never disagree
   * (product owner, 2026-09-15: the tree and the department list read as
   * two conflicting sources with no reasoning shown between them). The badge
   * sits on the person's own tree row rather than only in the list below, so
   * headship is visible exactly where the reporting line already shows them.
   */
  const headOf = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const department of answer?.departments ?? []) {
      if (!department.headEmploymentId) continue;
      map.set(department.headEmploymentId, [...(map.get(department.headEmploymentId) ?? []), department.name]);
    }
    return map;
  }, [answer]);

  const openMove = (node: OrgChartNode, newBoss?: OrgChartNode) => {
    setMove({
      employmentId: node.employmentId,
      personName: node.label,
      bossEmploymentId: newBoss?.employmentId ?? node.bossEmploymentId ?? '',
      // 🔨 The department is prefilled from the manager the drop named, and stays editable.
      departmentId: newBoss?.departmentId ?? node.departmentId ?? '',
      effectiveOn: today,
      reason: '',
      replacementHeadEmploymentId: '',
      skillLevelId: node.skillLevelId ?? '',
      heldLevel: node.skillLevelId ? { id: node.skillLevelId, name: node.skillLevelName ?? '' } : null,
    });
  };

  const saveMove = async () => {
    if (!move) return;
    const sent = await post(ORG_MOVE_PATH, {
      employmentId: move.employmentId,
      bossEmploymentId: move.bossEmploymentId || null,
      departmentId: move.departmentId || null,
      effectiveOn: move.effectiveOn,
      reason: move.reason.trim(),
      replacementHeadEmploymentId: move.replacementHeadEmploymentId || null,
      skillLevelId: move.skillLevelId || null,
    }, apiBaseUrl);
    setNotice(sent.ok ? { tone: 'good', text: `${t('org.moveSaved')} · ${move.personName}` } : { tone: 'error', text: sent.message });
    if (sent.ok) {
      setMove(null);
      fetched.reload();
    }
  };

  const saveHead = async () => {
    if (!head) return;
    const sent = await post<{ adminTasksRaised?: number }>(ORG_DEPARTMENT_HEAD_PATH, {
      departmentId: head.departmentId,
      headEmploymentId: head.headEmploymentId || null,
      reason: head.reason.trim(),
    }, apiBaseUrl);
    if (sent.ok) {
      const raised = sent.data.adminTasksRaised ?? 0;
      setNotice({ tone: 'good', text: line(t('org.headSaved'), '·', head.departmentName, raised > 0 ? `· ${raised} ${t('org.headTasks')}` : null) });
      setHead(null);
      fetched.reload();
    } else {
      setNotice({ tone: 'error', text: sent.message });
    }
  };

  const personOptions = (exclude?: string) => [
    { value: '', label: t('org.noManager') },
    ...cast.filter((node) => node.employmentId !== exclude).map((node) => ({ value: node.employmentId, label: line(node.label, node.title ? `— ${node.title}` : null) })),
  ];

  /** 🔒 No Level, then the active levels lowest first, and the level held where it has been retired since (T441; decision 1123). */
  const levelOptions = (draft: MoveDraft) => {
    const active = answer?.skillLevels ?? [];
    const held = draft.heldLevel && !active.some((row) => row.id === draft.heldLevel!.id) ? [{ value: draft.heldLevel.id, label: draft.heldLevel.name }] : [];
    return [{ value: '', label: t('org.noLevel') }, ...held, ...active.map((row) => ({ value: row.id, label: row.name }))];
  };

  /** The head badge, drawn wherever the person stands: on their card, or in the Unplaced group. */
  const headBadges = (node: OrgChartNode) => (
    <>
      {headOf.get(node.employmentId)?.map((name) => (
        <Pill key={name} text={[t('org.headOfPrefix'), name].join(' ')} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} asTyped />
      ))}
    </>
  );

  const moveAction = (node: OrgChartNode) => (
    mayEdit ? <Button size={Size.Small} color={Color.Inactive} text={[t('org.movePerson')]} actions={[() => openMove(node)]} /> : null
  );

  const rowActions = (node: OrgChartNode) => (
    <>
      {headBadges(node)}
      {moveAction(node)}
    </>
  );

  return (
    <PageModule
      title={t('org.title')}
      subtitle={pageKindLineText({ page: 'named', route: '/org' })}
      controls={
        <span className={styles.controls}>
          <DatePicker size={Size.Small} label={t('org.asAt')} value={onDate} onChange={setOnDate} name="orgChartOnDate" />
        </span>
      }
    >
      <div className={styles.page} data-page="org-chart">
        {noticeBanner}
        <AsyncStatePanel state={fetched.state} emptyAction={t('org.empty')} onRetry={fetched.reload}>
          {(chart) => (
            <div className={styles.page} data-org-chart-date={chart.onDate} data-org-can-edit={mayEdit ? 'true' : 'false'}>
              {mayEdit ? null : <ReadOnlyNote what={t('org.readOnly.what')} needs={t('org.readOnly')} data-org-read-only="true" />}
              {chart.unplaced.length > 0 ? (
                <section className={styles.section} data-org-section="unplaced">
                  <h2 className={styles.heading}>{t('org.unplaced')}</h2>
                  <p className={styles.meta}>
                    {t('org.unplacedHint')} <Link to={ORG_EXCEPTIONS_ROUTE}>{t('org.exceptionsLink')}</Link>
                    {chart.exceptionCount > 0 ? ` (${chart.exceptionCount})` : null}
                  </p>
                  <ul className={styles.list}>
                    {chart.unplaced.map((node) => (
                      <li key={node.employmentId} className={styles.item} data-org-unplaced={node.employmentId}>
                        <span className={styles.itemMain}>
                          <span className={styles.itemName}>{node.label}</span>
                          <span className={styles.meta}>{nodeMeta(node)}</span>
                        </span>
                        {rowActions(node)}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              <section className={styles.section} data-org-section="tree">
                <OrgTree
                  nodes={chart.nodes}
                  label={t('org.title')}
                  renderMeta={nodeMeta}
                  renderBadges={headBadges}
                  renderActions={moveAction}
                  {...(mayEdit ? { onDropOn: (dragged: OrgChartNode, target: OrgChartNode) => openMove(dragged, target) } : {})}
                />
                {chart.nodes.length === 0 ? <p className={styles.meta}>{t('org.empty')}</p> : null}
              </section>
              <section className={styles.section} data-org-section="departments">
                <h2 className={styles.heading}>{t('org.departments')}</h2>
                <p className={styles.meta}>{t('org.departmentsHint')}</p>
                <ul className={styles.list}>
                  {chart.departments.map((department) => (
                    <li key={department.id} className={styles.item} data-org-department={department.id}>
                      <span className={styles.itemMain}>
                        <span className={styles.itemName}>{department.name}</span>
                        <span className={styles.meta}>{department.headName ?? t('org.noHead')}</span>
                      </span>
                      {mayEdit ? (
                        <Button
                          size={Size.Small}
                          color={Color.Inactive}
                          text={[t('org.changeHead')]}
                          actions={[() => setHead({ departmentId: department.id, departmentName: department.name, headEmploymentId: department.headEmploymentId ?? '', reason: '' })]}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </AsyncStatePanel>

        {/* 🔨 Keyed to what it edits, so reopening it on another person never shows the first person's draft. */}
        {move ? (
          <Modal
            key={move.employmentId}
            open
            title={t('org.moveTitle')}
            subtitle={move.personName}
            dataModal="org-move"
            onCancel={() => setMove(null)}
            actions={[
              { kind: 'primary', label: t('modal.save'), onAct: () => void saveMove(), disabled: move.reason.trim() === '' },
              { kind: 'cancel', label: t('modal.cancel'), onAct: () => setMove(null) },
            ]}
          >
            <div className={styles.fields}>
              <Select size={Size.Medium} label={t('org.newManager')} value={move.bossEmploymentId} onChange={(value) => setMove({ ...move, bossEmploymentId: value })} options={personOptions(move.employmentId)} dataAttribute={{ 'data-org-move-boss': 'true' }} />
              <DatePicker size={Size.Small} label={t('org.effectiveDate')} value={move.effectiveOn} onChange={(value) => setMove({ ...move, effectiveOn: value })} max={today} required name="orgMoveEffectiveOn" />
              <Select size={Size.Medium} label={t('org.department')} value={move.departmentId} onChange={(value) => setMove({ ...move, departmentId: value })} options={(answer?.departments ?? []).map((row) => ({ value: row.id, label: row.name }))} placeholder={t('org.noManager')} dataAttribute={{ 'data-org-move-department': 'true' }} />
              {/* 🔒 The worker's level, carried by the new dated position this window writes (T441; decision 1123). */}
              <Select
                size={Size.Medium}
                label={t('org.level')}
                value={move.skillLevelId}
                onChange={(value) => setMove({ ...move, skillLevelId: value })}
                options={levelOptions(move)}
                dataAttribute={{ 'data-org-move-level': 'true' }}
              />
              <TextInput size={Size.Large} label={t('org.reason')} value={move.reason} onChange={(value) => setMove({ ...move, reason: value })} required />
              <Select size={Size.Medium} label={t('org.replacementHead')} value={move.replacementHeadEmploymentId} onChange={(value) => setMove({ ...move, replacementHeadEmploymentId: value })} options={personOptions(move.employmentId)} />
              <p className={styles.meta}>{t('org.pastDateNote')}</p>
            </div>
          </Modal>
        ) : null}

        {head ? (
          <Modal
            key={head.departmentId}
            open
            title={t('org.headTitle')}
            subtitle={head.departmentName}
            dataModal="org-head"
            onCancel={() => setHead(null)}
            actions={[
              { kind: 'primary', label: t('modal.save'), onAct: () => void saveHead(), disabled: head.reason.trim() === '' },
              { kind: 'cancel', label: t('modal.cancel'), onAct: () => setHead(null) },
            ]}
          >
            <div className={styles.fields}>
              <Select size={Size.Medium} label={t('org.head')} value={head.headEmploymentId} onChange={(value) => setHead({ ...head, headEmploymentId: value })} options={personOptions()} dataAttribute={{ 'data-org-head-employment': 'true' }} />
              <TextInput size={Size.Large} label={t('org.reason')} value={head.reason} onChange={(value) => setHead({ ...head, reason: value })} required />
            </div>
          </Modal>
        ) : null}
      </div>
    </PageModule>
  );
}
